// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./WorkloadViewerConsole.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, deleteAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// CUSTOM COMPONENTS
import TanStackTable from "../../Misc/Table/TanStackTable";
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import AddAdminJobTitleDialog from "./AddAdminJobTitleDialog/AddAdminJobTitleDialog";

// CONSTANTS
import {
	defaultSort,
} from "../../Misc/Table/TanStackTableConstants";

const WorkloadViewerConsole = () => {
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// Data
	const [adminJobTitles, setAdminJobTitles] = useState([]);
	const [allJobTitles, setAllJobTitles] = useState([]);

	// Table
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	// UI state
	const [loading, setLoading] = useState(false);
	const [addOpen, setAddOpen] = useState(false);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Workload Viewer Console",
			employeeID: employeeID,
			notes: "Accessed the Workload Viewer Console page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchAdminJobTitles();
		fetchAllJobTitles();
	}, []);

	useEffect(() => {
		buildColumns();
		buildRows();
	}, [adminJobTitles]);

	const fetchAdminJobTitles = async () => {
		setLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/workload/admin-job-titles"
			);
			setAdminJobTitles(res.data || []);
		} catch (err) {
			console.error("Error fetching workload admin job titles:", err);
		}
		setLoading(false);
	};

	const fetchAllJobTitles = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobTitles/all"
			);
			setAllJobTitles(res.data || []);
		} catch (err) {
			console.error("Error fetching job titles:", err);
		}
	};

	const handleRemove = async (record) => {
		try {
			await deleteAPICall(
				instance,
				accounts[0],
				`/api/workload/admin-job-titles/${record.id}`
			);
			fetchAdminJobTitles();
		} catch (err) {
			console.error("Error removing admin job title:", err);
		}
	};

	const buildColumns = () => {
		setColumns([
			{
				accessorKey: "job_title_name",
				header: () => <span>Job Title</span>,
				label: "Job Title",
				cell: (props) => <span>{props.getValue()}</span>,
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "remove",
				header: "",
				label: "Remove",
				cell: ({ row }) => (
					<IconButton
						onClick={() => handleRemove(row.original.record)}
						size="small"
						disableRipple
						sx={{ justifyContent: "flex-end" }}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const buildRows = () => {
		setRows(
			adminJobTitles.map((jt) => ({
				job_title_name: jt.job_title_name,
				remove: "",
				record: jt,
			}))
		);
	};

	// Job titles not yet marked as admin, available to add
	const availableJobTitles = allJobTitles.filter(
		(jt) => !adminJobTitles.some((a) => a.job_title_id === jt.id)
	);

	return (
		<div className="WorkloadViewerConsole">
			<div className="WorkloadViewerConsoleHeader">
				<div className="WorkloadViewerConsoleTitle">
					Workload Viewer Console
					<Button
						variant="contained"
						color="primary"
						onClick={() => setAddOpen(true)}
						disabled={loading}
					>
						Add Job Title
					</Button>
				</div>
				<span className="WorkloadViewerConsoleDescription">
					Job titles listed here are <strong>excluded</strong> from the default employee selection in the Workload Viewer. They can still be selected manually via the People filter.
				</span>
			</div>

		{loading ? (
				<div className="loadingDiv">
					<CircularProgress />
				</div>
			) : adminJobTitles.length === 0 ? (
				<J2NoResultsFound />
			) : (
				<div className="tableContainerDiv">
					<TanStackTable columnData={columns} rowData={rows} />
				</div>
			)}

			<AddAdminJobTitleDialog
				open={addOpen}
				availableJobTitles={availableJobTitles}
				onClose={() => setAddOpen(false)}
				onAdded={fetchAdminJobTitles}
			/>
		</div>
	);
};

export default WorkloadViewerConsole;
